<template>
  <div class="position-relative p-3">
    <b-row>
      <b-col md="6">
        <b-card shadow-sm class="mb-4 border-0 position-relative overflow-hidden glass-card">
          <div v-if="volumeLoading" class="loading-overlay">
            <b-spinner small variant="primary"></b-spinner>
          </div>
          <div :class="{'loading-blur': volumeLoading}">
            <h6 class="font-weight-bold mb-3"><md-icon size="18" class="mr-1">trending_up</md-icon> Trip Volume Trends</h6>
            <apexchart type="area" height="280" :options="tripVolumeOptions" :series="tripVolumeSeries"></apexchart>
          </div>
        </b-card>
      </b-col>
      <b-col md="6">
        <b-card shadow-sm class="mb-4 border-0 position-relative overflow-hidden glass-card">
          <div v-if="availabilityLoading" class="loading-overlay">
            <b-spinner small variant="primary"></b-spinner>
          </div>
          <div :class="{'loading-blur': availabilityLoading}">
            <h6 class="font-weight-bold mb-3"><md-icon size="18" class="mr-1">donut_large</md-icon> Fleet Availability Ratio</h6>
            <apexchart type="radialBar" height="280" :options="availabilityOptions" :series="availabilitySeries"></apexchart>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="6">
        <b-card shadow-sm class="mb-4 border-0 position-relative overflow-hidden glass-card">
          <div v-if="activityLoading" class="loading-overlay">
            <b-spinner small variant="primary"></b-spinner>
          </div>
          <div :class="{'loading-blur': activityLoading}">
            <h6 class="font-weight-bold mb-3"><md-icon size="18" class="mr-1">insert_chart</md-icon> Vehicle-wise Trip Activity (Top 10)</h6>
            <apexchart type="bar" height="280" :options="vehicleTripsOptions" :series="vehicleTripsSeries"></apexchart>
          </div>
        </b-card>
      </b-col>
      <b-col md="6">
        <b-card shadow-sm class="mb-4 border-0 position-relative overflow-hidden glass-card" bg-variant="light">
          <div v-if="auditLoading" class="loading-overlay">
            <b-spinner small variant="primary"></b-spinner>
          </div>
          <div :class="{'loading-blur': auditLoading}">
            <h6 class="text-primary font-weight-bold mb-3"><md-icon size="18" class="mr-1">account_balance</md-icon> Transport Accounting Audit</h6>
            
            <div class="audit-stats-container p-3 rounded bg-white shadow-sm mb-3">
              <div class="row text-center my-3">
                <div class="col-4 border-right">
                  <div class="text-muted xs-font mb-1">TOTAL TRIPS</div>
                  <div class="h5 font-weight-bold mb-0">{{ auditStats.totalTrips }}</div>
                </div>
                <div class="col-4 border-right">
                  <div class="text-muted xs-font mb-1 text-success">BILLED</div>
                  <div class="h5 font-weight-bold mb-0">{{ auditStats.billedTrips }}</div>
                </div>
                <div class="col-4">
                  <div class="text-muted xs-font mb-1 text-warning">UNBILLED</div>
                  <div class="h5 font-weight-bold mb-0">{{ auditStats.unbilledTrips }}</div>
                </div>
              </div>

              <div class="bg-light p-2 rounded text-center mb-3">
                <div class="xs-font text-muted mb-1 text-uppercase letter-spacing-1">Total Billed Revenue</div>
                <div class="h3 font-weight-bold mb-0 text-primary">₹{{ Number(auditStats.billedRevenue || 0).toLocaleString('en-IN') }}</div>
              </div>
              
              <b-progress class="mb-3" height="8px" show-progress animated>
                <b-progress-bar :value="auditStats.billedTrips" :max="auditStats.totalTrips" variant="success"></b-progress-bar>
                <b-progress-bar :value="auditStats.unbilledTrips" :max="auditStats.totalTrips" variant="warning"></b-progress-bar>
              </b-progress>

              <div class="d-flex justify-content-between xs-font text-muted">
                <span><span class="dot healthy mr-1"></span> Completion: <strong>{{ auditStats.totalTrips > 0 ? Math.round((auditStats.billedTrips/auditStats.totalTrips)*100) : 0 }}%</strong></span>
                <span>Target: 100%</span>
              </div>
            </div>

            <div class="alert alert-info border-0 py-2 px-3 small d-flex align-items-center mb-0">
               <md-icon size="18" class="mr-2 text-info">info</md-icon>
               <span>This audit monitors trips marked as completed but awaiting final billing execution in Tally records.</span>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="12">
        <b-card shadow-sm class="mb-4 border-0 position-relative overflow-hidden glass-card">
          <div v-if="fuelLoading" class="loading-overlay">
            <b-spinner small variant="primary"></b-spinner>
          </div>
          <div :class="{'loading-blur': fuelLoading}">
            <h6 class="font-weight-bold mb-3"><md-icon size="18" class="mr-1">speed</md-icon> Fuel Efficiency Trend (Avg KM/L)</h6>
            <apexchart type="line" height="280" :options="fuelTrendOptions" :series="fuelTrendSeries"></apexchart>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
export default {
  name: 'OperationsIntelligenceTab',
  props: {
    tripVolumeSeries: { type: Array, required: true },
    tripVolumeOptions: { type: Object, required: true },
    availabilitySeries: { type: Array, required: true },
    availabilityOptions: { type: Object, required: true },
    vehicleTripsSeries: { type: Array, required: true },
    vehicleTripsOptions: { type: Object, required: true },
    fuelTrendSeries: { type: Array, required: true },
    fuelTrendOptions: { type: Object, required: true },
    auditStats: { type: Object, default: () => ({ totalTrips: 0, billedTrips: 0, unbilledTrips: 0, billedRevenue: 0 }) },
    volumeLoading: { type: Boolean, default: false },
    availabilityLoading: { type: Boolean, default: false },
    activityLoading: { type: Boolean, default: false },
    auditLoading: { type: Boolean, default: false },
    fuelLoading: { type: Boolean, default: false }
  }
};
</script>

<style scoped>
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  backdrop-filter: blur(2px);
}
.loading-blur {
  filter: blur(1px);
  opacity: 0.6;
}
.bg-light-primary { background-color: #e9ecef !important; color: #3b82f6 !important; }
.health-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
  padding: 10px;
}
.health-node {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}
.health-node.healthy { background-color: #10b981; }
.health-node.warning { background-color: #f59e0b; }
.health-node.critical { background-color: #ef4444; }
.health-node:hover {
  transform: scale(1.2);
  z-index: 10;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 4px;
}
.dot.healthy { background-color: #10b981; }
.dot.warning { background-color: #f59e0b; }
.dot.critical { background-color: #ef4444; }
.xs-font {
  font-size: 0.65rem;
  letter-spacing: 0.08em;
}
</style>



// WEBPACK FOOTER //
// OperationsIntelligenceTab.vue?2a06265e